import React, { useState, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Badge, Form, Spinner, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import jsPDF from "jspdf";

const API = process.env.REACT_APP_API;

const GRADE_THRESHOLDS = [
  { min: 80, grade: 4 }, { min: 75, grade: 3.5 }, { min: 70, grade: 3 },
  { min: 65, grade: 2.5 }, { min: 60, grade: 2 }, { min: 55, grade: 1.5 },
  { min: 50, grade: 1 },  { min: 0, grade: 0 },
];

const calcGrade = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const s = parseFloat(score);
  if (isNaN(s)) return null;
  for (const t of GRADE_THRESHOLDS) { if (s >= t.min) return t.grade; }
  return 0;
};

const GradeCell = ({ entry }) => {
  if (!entry || entry.g_grade === null) return <span style={{ color: '#aaa' }}>-</span>;
  const grade = calcGrade(entry.g_grade);
  if (entry.g_waiting === '1') {
    return <><span style={{ color: 'blue' }}>{entry.g_grade}</span>{' '}<span style={{ color: 'red', fontWeight: 'bold' }}>ร</span></>;
  }
  return (
    <>
      <span style={{ color: 'blue' }}>{entry.g_grade}</span>
      {grade !== null && (
        <span className="ms-1">
          <Badge bg={grade >= 2 ? 'success' : grade >= 1 ? 'warning' : 'danger'} style={{ fontSize: '0.7em' }}>{grade}</Badge>
        </span>
      )}
    </>
  );
};

const SemesterTable = ({ rows, semester, onEdit }) => {
  const filtered = rows.filter(r => String(r.sub_semester) === String(semester));
  return (
    <Table responsive bordered hover size="sm" className="mb-0">
      <thead>
        <tr className="text-center">
          <th colSpan={6}>ภาคเรียนที่ {semester}</th>
        </tr>
        <tr>
          <th className="text-center" style={{ width: 90 }}>รหัสวิชา</th>
          <th>ชื่อวิชา</th>
          <th className="text-center" style={{ width: 150 }}>ครูผู้สอน</th>
          <th className="text-center" style={{ width: 60 }}>คะแนน</th>
          <th className="text-center" style={{ width: 70 }}>ผลการเรียน</th>
          <th style={{ width: 60 }}></th>
        </tr>
      </thead>
      <tbody>
        {filtered.length > 0 ? filtered.map(r => (
          <tr key={`${r.teach_id}-${r.sub_id}`}>
            <td className="text-center">{r.sub_subid}</td>
            <td>
              {r.sub_subname}
              {r.sub_type === 'extra' && <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>(เพิ่มเติม)</span>}
            </td>
            <td className="text-center">{r.teacher_name}</td>
            <td className="text-center" style={{ color: 'blue' }}>{r.g_grade ?? '-'}</td>
            <td className="text-center">
              {r.g_id === null || r.g_id === undefined ? '-'
                : r.g_waiting === '1'
                  ? <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span>
                  : calcGrade(r.g_grade) !== null
                    ? <Badge bg={calcGrade(r.g_grade) >= 2 ? 'success' : calcGrade(r.g_grade) >= 1 ? 'warning' : 'danger'}>{calcGrade(r.g_grade)}</Badge>
                    : '-'
              }
            </td>
            <td className="text-center">
              <Button variant="link" size="sm" className="p-0" onClick={() => onEdit(r)}>
                <i className="ri-edit-2-fill"></i> แก้ไข
              </Button>
            </td>
          </tr>
        )) : (
          <tr><td colSpan={6} className="text-center text-muted py-2">ไม่พบข้อมูล</td></tr>
        )}
      </tbody>
    </Table>
  );
};

export default function StudentsGrades() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const [searchId, setSearchId]     = useState('');
  const [result, setResult]         = useState(null);   // { student, rows }
  const [searching, setSearching]   = useState(false);
  const [notFound, setNotFound]     = useState(false);

  const [showModal, setShowModal]       = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData]       = useState(null);
  const [score, setScore]               = useState('');
  const [waiting, setWaiting]           = useState(false);

  const handleEditClick = (row) => {
    setShowModal(true);
    setModalLoading(true);
    setModalData(null);
    axiosInstance.post(`${API}/prepareGradeEdit`, {
      st_id:    result.student.st_id,
      teach_id: row.teach_id,
      sub_id:   row.sub_id,
      study_id: row.study_id,
    }, { headers: { authorization: `Bearer ${getToken()}` } })
    .then(res => {
      const d = res.data;
      setModalData({ ...d, teach_id: row.teach_id, sub_id: row.sub_id, study_id: row.study_id });
      setScore(d.g_grade !== null && d.g_grade !== undefined ? String(d.g_grade) : '');
      setWaiting(d.g_waiting === '1');
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลได้' });
      setShowModal(false);
    })
    .finally(() => setModalLoading(false));
  };

  const handleSave = () => {
    setModalLoading(true);
    axiosInstance.put(`${API}/updateGrade`, {
      g_id:      modalData.g_id,
      g_grade:   score,
      g_waiting: waiting ? '1' : '0',
      update_by: String(getUserId()),
    }, { headers: { authorization: `Bearer ${getToken()}` } })
    .then(() => {
      setResult(prev => ({
        ...prev,
        rows: prev.rows.map(r =>
          r.teach_id === modalData.teach_id && r.sub_id === modalData.sub_id && r.study_id === modalData.study_id
            ? { ...r, g_grade: score === '' ? null : parseFloat(score), g_waiting: waiting ? '1' : '0', g_id: modalData.g_id }
            : r
        ),
      }));
      setShowModal(false);
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
    })
    .finally(() => setModalLoading(false));
  };

  const previewGrade = calcGrade(score);

  const handleSearch = () => {
    if (!searchId.trim()) return;
    setSearching(true);
    setResult(null);
    setNotFound(false);
    axiosInstance.get(`${API}/getStudentGradesByIdNumber/${searchId.trim()}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => setResult(res.data))
    .catch(err => {
      if (err.response?.status === 404) setNotFound(true);
    })
    .finally(() => setSearching(false));
  };

  const resultsRef = useRef(null);

  const PRINT_STYLES = `
    body { font-family: 'Sarabun', Arial, sans-serif; font-size: 9.5px; margin: 0; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 0; }
    th, td { border: 1px solid #bbb; padding: 2px 4px; }
    th { background: #eef2ff; }
    @media print { @page { margin: 8mm; } }`;

  const buildSemTable = (rows, sem) => {
    const filtered = rows.filter(r => String(r.sub_semester) === String(sem));
    const bodyRows = filtered.length > 0
      ? filtered.map(r => {
          const grade = r.g_id === null || r.g_id === undefined ? '-'
            : r.g_waiting === '1' ? '<span style="color:red;font-weight:bold;">ร</span>'
            : calcGrade(r.g_grade) !== null ? calcGrade(r.g_grade) : '-';
          return `<tr>
            <td style="text-align:center">${r.sub_subid}</td>
            <td>${r.sub_subname}${r.sub_type === 'extra' ? ' <small>(เพิ่มเติม)</small>' : ''}</td>
            <td style="text-align:center">${r.teacher_name ?? ''}</td>
            <td style="text-align:center">${r.g_grade ?? '-'}</td>
            <td style="text-align:center">${grade}</td>
          </tr>`;
        }).join('')
      : `<tr><td colspan="5" style="text-align:center;color:#888;">ไม่พบข้อมูล</td></tr>`;
    return `<table>
      <thead>
        <tr><th colspan="5" style="text-align:center;">ภาคเรียนที่ ${sem}</th></tr>
        <tr>
          <th style="text-align:center;width:90px;">รหัสวิชา</th>
          <th>ชื่อวิชา</th>
          <th style="text-align:center;width:140px;">ครูผู้สอน</th>
          <th style="text-align:center;width:60px;">คะแนน</th>
          <th style="text-align:center;width:70px;">ผลการเรียน</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>`;
  };

  const buildStudentHeader = () => `
    <div style="margin-bottom:6px;padding:4px 0;border-bottom:1px solid #ccc;font-size:10px;">
      <strong>เลขประจำตัว:</strong> ${result.student.st_idnumber}&nbsp;&nbsp;
      <strong>ชื่อ-สกุล:</strong> ${result.student.st_name}
    </div>`;

  const buildPageHtml = (group) => `
    <div style="padding:10px;">
      ${buildStudentHeader()}
      <h3 style="font-size:11px;margin:5px 0;">${group.c_class} — ปีการศึกษา ${group.year_year}</h3>
      <div style="margin-bottom:10px;">${buildSemTable(group.rows, 1)}</div>
      ${buildSemTable(group.rows, 2)}
    </div>`;

  const handleExportExcel = async () => {
    const XLSX = await import('xlsx');
    const wsData = [];
    wsData.push(['เลขประจำตัว', result.student.st_idnumber, '', 'ชื่อ-สกุล', result.student.st_name]);
    wsData.push([]);
    classGroups.forEach(group => {
      wsData.push([`${group.c_class} — ปีการศึกษา ${group.year_year}`]);
      wsData.push([]);
      [1, 2].forEach(sem => {
        const semRows = group.rows.filter(r => String(r.sub_semester) === String(sem));
        wsData.push([`ภาคเรียนที่ ${sem}`]);
        wsData.push(['รหัสวิชา', 'ชื่อวิชา', 'ประเภท', 'ครูผู้สอน', 'คะแนน', 'ผลการเรียน']);
        if (semRows.length > 0) {
          semRows.forEach(r => {
            const grade = r.g_id === null || r.g_id === undefined ? '-'
              : r.g_waiting === '1' ? 'ร'
              : calcGrade(r.g_grade) !== null ? calcGrade(r.g_grade) : '-';
            wsData.push([r.sub_subid, r.sub_subname, r.sub_type === 'extra' ? 'เพิ่มเติม' : 'พื้นฐาน', r.teacher_name, r.g_grade ?? '', grade]);
          });
        } else {
          wsData.push(['ไม่พบข้อมูล']);
        }
        wsData.push([]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ผลการเรียน');
    XLSX.writeFile(wb, `ผลการเรียน_${result.student.st_idnumber}_${result.student.st_name}.xlsx`);
  };

  const handleExportPDF = async () => {
    if (!result || classGroups.length === 0) return;
    const html2canvas = (await import('html2canvas')).default;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    for (let i = 0; i < classGroups.length; i++) {
      if (i > 0) pdf.addPage();
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;background:#fff;width:794px;';
      wrapper.innerHTML = `<style>${PRINT_STYLES}</style>${buildPageHtml(classGroups[i])}`;
      document.body.appendChild(wrapper);
      const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true });
      document.body.removeChild(wrapper);
      const imgData = canvas.toDataURL('image/png');
      const imgW = pageWidth - 10;
      const imgH = (canvas.height * imgW) / canvas.width;
      let y = 5;
      let remaining = imgH;
      while (remaining > 0) {
        pdf.addImage(imgData, 'PNG', 5, y, imgW, imgH);
        remaining -= (pageHeight - 10);
        if (remaining > 0) { pdf.addPage(); y = 5 - (imgH - remaining); }
      }
    }
    pdf.save(`ผลการเรียน_${result.student.st_idnumber}_${result.student.st_name}.pdf`);
  };

  const handlePrint = () => {
    if (!result || classGroups.length === 0) return;
    const pages = classGroups.map((group, i) => {
      const pageBreak = i < classGroups.length - 1 ? 'page-break-after:always;' : '';
      return `<div style="${pageBreak}">${buildPageHtml(group)}</div>`;
    }).join('');
    const win = window.open('', '_blank', 'width=960,height=700');
    win.document.write(`
      <html><head><title>ผลการเรียน ${result.student.st_idnumber}</title>
      <style>${PRINT_STYLES}</style>
      </head><body onload="window.print()">${pages}</body></html>
    `);
    win.document.close();
  };

  // Group rows by class: [{ c_id, c_class, year_year, rows[] }]
  const classGroups = React.useMemo(() => {
    if (!result?.rows) return [];
    const map = new Map();
    result.rows.forEach(r => {
      const key = `${r.c_id}-${r.year_id}`;
      if (!map.has(key)) map.set(key, { c_id: r.c_id, c_class: r.c_class, year_year: r.year_year, rows: [] });
      map.get(key).rows.push(r);
    });
    return Array.from(map.values()).sort((a, b) => a.c_id - b.c_id);
  }, [result]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">วัดผลและประเมินผล</Link></li>
              <li className="breadcrumb-item active">ผลการเรียนรายนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ผลการเรียนรายนักเรียน</h4>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <Form className="d-flex gap-2" onSubmit={e => { e.preventDefault(); handleSearch(); }}>
                  <Form.Control
                    style={{ maxWidth: 260 }}
                    placeholder="กรอกเลขประจำตัวนักเรียน"
                    value={searchId}
                    onChange={e => { setSearchId(e.target.value); setNotFound(false); setResult(null); }}
                  />
                  <Button variant="primary" type="submit" disabled={searching || !searchId.trim()}>
                    {searching ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-line me-1"></i>ค้นหา</>}
                  </Button>
                </Form>
                {notFound && (
                  <div className="text-danger mt-2">
                    <i className="ri-close-circle-line me-1"></i>ไม่พบนักเรียนที่มีเลขประจำตัว {searchId}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {result && (
          <div ref={resultsRef}>
            <div className="mb-3" data-export="student-info">
              <Card className="card-one">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div>
                      <span className="text-muted fs-sm">เลขประจำตัว</span>
                      <div className="fw-semibold">{result.student.st_idnumber}</div>
                    </div>
                    <div>
                      <span className="text-muted fs-sm">ชื่อ-สกุล</span>
                      <div className="fw-semibold">{result.student.st_name}</div>
                    </div>
                    <div>
                      <span className="text-muted fs-sm">สถานะ</span>
                      <div>
                        {result.student.st_status === '0'
                          ? <Badge bg="danger">พ้นสภาพ</Badge>
                          : result.student.st_status === '1'
                            ? <Badge bg="primary">กำลังศึกษา</Badge>
                            : <Badge bg="success">นักเรียนใหม่</Badge>}
                      </div>
                    </div>
                    <div className="ms-auto d-flex gap-2">
                      <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
                        <i className="ri-file-excel-2-line me-1"></i>ส่งออก EXCEL
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={handleExportPDF}>
                        <i className="ri-file-pdf-line me-1"></i>ส่งออก PDF
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
                        <i className="ri-printer-line me-1"></i>ปริ้น
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>

            {classGroups.length === 0 ? (
              <div className="text-center text-muted py-4">ไม่พบข้อมูลผลการเรียน</div>
            ) : classGroups.map(group => (
              <div key={`${group.c_id}-${group.year_year}`} data-export="group-card">
                <Card className="card-one mb-3">
                  <Card.Header>
                    <Card.Title as="h6">
                      <i className="ri-graduation-cap-line me-1"></i>
                      {group.c_class} — ปีการศึกษา {group.year_year}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-2">
                    <Row className="g-3">
                      <Col xl="6">
                        <SemesterTable rows={group.rows} semester="1" onEdit={handleEditClick} />
                      </Col>
                      <Col xl="6">
                        <SemesterTable rows={group.rows} semester="2" onEdit={handleEditClick} />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} size="sm" centered>
          <Modal.Header closeButton>
            <Modal.Title as="h6"><i className="ri-edit-2-fill me-1"></i>แก้ไขคะแนน</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading && !modalData ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /> กำลังโหลด...</div>
            ) : modalData && (
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">นักเรียน</Form.Label>
                  <Form.Control size="sm" value={result ? `${result.student.st_idnumber} ${result.student.st_name}` : ''} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">วิชา</Form.Label>
                  <Form.Control size="sm" value={`${modalData.sub_subid || ''} ${modalData.sub_subname || ''}`} disabled />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 fw-semibold">
                    คะแนน
                    {previewGrade !== null && score !== '' && (
                      <span className="ms-2 text-muted fw-normal">
                        → ผลการเรียน: <strong style={{ color: 'blue' }}>{previewGrade}</strong>
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    size="sm" type="number" min="0" max="100" step="0.5"
                    value={score} onChange={e => setScore(e.target.value)}
                    placeholder="กรอกคะแนน 0–100" autoFocus
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox" id="waiting-check-sg"
                  label={<>ติดค้าง กรณีที่นักเรียนติด <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span></>}
                  checked={waiting} onChange={e => setWaiting(e.target.checked)}
                />
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
              <i className="ri-close-line"></i> ปิด
            </Button>
            <Button variant="success" size="sm" onClick={handleSave} disabled={modalLoading || !modalData}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : <><i className="ri-check-line"></i> บันทึก</>}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
